import { useMemo } from 'react';
import { Empty, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { SorterResult } from 'antd/es/table/interface';

import InfiniteScroller from 'components/molecules/InfiniteScroller';

import type { PromoLibraryRow } from './types';

const EMPTY_IMAGE = Empty.PRESENTED_IMAGE_SIMPLE;
const FALLBACK_COLUMN_WIDTH = 120;
const TABLE_HEIGHT_OFFSET = 360;

/**
 * Rows are identified by the backend's `row_id` when present; otherwise the
 * attribute values (the scalar fields) form a content key, since the attribute
 * combination is what makes a library row unique.
 */
const resolveRowKey = (row: PromoLibraryRow): string => {
    if (row.row_id) return row.row_id;
    const parts: string[] = [];
    for (const [field, value] of Object.entries(row)) {
        if (value === null || typeof value !== 'object') parts.push(`${field}=${String(value)}`);
    }
    return parts.join('|');
};

export interface PromoLibraryTableProps {
    dataSource: PromoLibraryRow[];
    columns: ColumnsType<PromoLibraryRow>;
    isPending: boolean;
    isFetching: boolean;
    isFetchingNextPage: boolean;
    hasNextPage: boolean;
    fetchNextPage: () => void;
    onChange: (
        pagination: unknown,
        filters: unknown,
        sorter: SorterResult<PromoLibraryRow> | SorterResult<PromoLibraryRow>[],
    ) => void;
    emptyDescription: string;
    emptySubtitle: string;
}

const PromoLibraryTable = ({
    dataSource,
    columns,
    isPending,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    onChange,
    emptyDescription,
    emptySubtitle,
}: PromoLibraryTableProps) => {
    const scrollX = useMemo(
        () => columns.reduce((sum, col) => sum + (typeof col.width === 'number' ? col.width : FALLBACK_COLUMN_WIDTH), 0),
        [columns],
    );

    return (
        <InfiniteScroller
            data={dataSource}
            onNext={fetchNextPage}
            hasMore={hasNextPage}
            loading={isFetchingNextPage}
            height={window.innerHeight - TABLE_HEIGHT_OFFSET}
        >
            <Table<PromoLibraryRow>
                rowKey={resolveRowKey}
                pagination={false}
                dataSource={dataSource}
                columns={columns}
                scroll={{ x: scrollX }}
                tableLayout="fixed"
                loading={isPending || isFetching || isFetchingNextPage}
                sticky
                bordered
                onChange={onChange}
                locale={{
                    emptyText: (
                        <Empty
                            image={EMPTY_IMAGE}
                            description={
                                <div className="flex flex-col items-center">
                                    <p className="text-sm font-medium text-gray-500">{emptyDescription}</p>
                                    <p className="mt-1 text-xs text-gray-400">{emptySubtitle}</p>
                                </div>
                            }
                        />
                    ),
                }}
            />
        </InfiniteScroller>
    );
};

PromoLibraryTable.displayName = 'PromoLibraryTable';

export default PromoLibraryTable;
